import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import './Grades.css';

const Grades = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getStudentEnrollments, getStudentGrades, calculateECTS } = useData();

  const userId = user?.id || user?._id;
  const enrollments = getStudentEnrollments(userId);
  const grades = getStudentGrades(userId);

  const gradesByCourse = useMemo(() => {
    const map = new Map();

    enrollments.forEach(enrollment => {
      const course = enrollment.course;
      const courseId = enrollment.courseId || course?.id;
      if (!courseId) return;

      const courseGrades = grades.filter(g => String(g.courseId || g.course?.id) === String(courseId));
      map.set(String(courseId), {
        course,
        grades: courseGrades,
        isPassed: courseGrades.length > 0 && courseGrades.every(g => g.status === 'passed')
      });
    });

    return Array.from(map.values());
  }, [enrollments, grades]);

  const totalPassed = grades.filter(g => g.status === 'passed').length;
  const totalEcts = calculateECTS(userId);

  return (
    <div className="grades-page">
      <div className="page-header">
        <h1>{t('nav.grades')}</h1>
      </div>

      <div className="grades-summary">
        <div className="summary-card">
          <span className="summary-value">{totalEcts}</span>
          <span className="summary-label">{t('student.ectsEarned')}</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{totalPassed}</span>
          <span className="summary-label">{t('grade.passed')}</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{grades.length}</span>
          <span className="summary-label">{t('grade.title')}</span>
        </div>
      </div>

      <div className="grades-list">
        {gradesByCourse.map((item, index) => (
          <div key={index} className="module-grade-card">
            <div className="module-header">
              <div className="module-info">
                <h3>{item.course?.name || '-'}</h3>
                <span className="module-code">{item.course?.code || '-'}</span>
              </div>
              <div className="module-ects">
                {item.course?.location || '-'}
              </div>
            </div>
            
            <div className="module-meta">
              <span>{t('course.startDate')}: {item.course?.startDate ? new Date(item.course.startDate).toLocaleDateString() : '-'}</span>
              <span>{t('course.endDate')}: {item.course?.endDate ? new Date(item.course.endDate).toLocaleDateString() : '-'}</span>
            </div>

            <div className="grades-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('exam.title')}</th>
                    <th>{t('grade.score')}</th>
                    <th>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {item.grades.map((grade, idx) => (
                    <tr key={idx}>
                      <td>{grade.notes || t(`exam.${grade.examType || 'title'}`, grade.examType || '-')}</td>
                      <td>
                        <span className={`score ${grade.status === 'passed' ? 'passed' : 'failed'}`}>
                          {typeof grade.grade === 'number' ? `${grade.grade.toFixed(1)}/10` : '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${grade.status}`}>
                          {t(`grade.${grade.status}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {item.grades.length === 0 && (
                    <tr>
                      <td colSpan="3" className="no-data">{t('common.noData')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="module-result">
              <span className={`result-badge ${item.isPassed ? 'passed' : 'failed'}`}>
                {item.isPassed ? t('grade.passed') : t('grade.failed')}
              </span>
            </div>
          </div>
        ))}
        
        {gradesByCourse.length === 0 && (
          <div className="no-grades">
            <p>{t('common.noData')}</p>
          </div>
        )}
      </div>

      <div className="passing-info">
        <h3>{t('grade.passingThreshold')}</h3>
        <p>5.5/10</p>
      </div>
    </div>
  );
};

export default Grades;
